import React, { Component } from 'react';

import Yaml from 'js-yaml';
import { connect } from 'react-redux';
import { Formik, Field, ErrorMessage } from 'formik';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as _ from 'lodash';
import PropTypes from 'prop-types';

import DataSection from './DataSection';
import FieldUtil from '../utils/FieldNames';
import { editPayerAction, restoreConfigVersionAction } from '../actions';
import { getSchema } from '../utils/Schema';

import '../../../../app/assets/stylesheets/rte_edit_payer.scss';
import PayerVersions from './PayerVersions';
import FormField from './FormField';
import CostRulesSection from './CostRulesSection';
import GroupMappingSection from './GroupMappingSection';
import PayerInfo from './PayerInfo';
import TwoSeventySection from './TwoSeventySection';
import TwoSeventyOneHeader from './TwoSeventyOneHeader';
import { toEditableFormat as payerRulesToEditableFormat } from '../utils/convert-to-editable';
import { evolve } from '../../utility/evolve';
import { toDbFormat } from '../utils/convert-to-db';


const payerToEditableFormat = evolve({
  payer_config: {
    active_flg: v => v.toUpperCase() === 'Y',
    payer_config_settings: payerRulesToEditableFormat
  }
});

class EditPayer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      visibleSection: 'two_seventy_one.GENMEDICAL.active_coverage',
      visibleTextArea: null
    };

    _.set(this.state, ['visible', 'two_seventy_one', 'GENMEDICAL', 'active_coverage'], true);

    this.updateVisibleSection = this.updateVisibleSection.bind(this);
    this.updateTwoSeventyOneVisibleSection = this.updateTwoSeventyOneVisibleSection.bind(this);

    this.initialPayerConfigValues = this.calculateInitialConfig();
  }

  get displayEditLink() {
    return this.props.canEdit && this.props.mode !== 'edit' && !this.props.isVersion;
  }

  get payerRules() {
    return _.get(this.props, 'payer.payer_config.payer_config_settings');
  }

  get costRulesActive() {
    return this.state.visibleSection === 'cost_rules';
  }

  get groupMappingActive() {
    return this.state.visibleSection === 'group';
  }

  get twoSeventyActive() {
    return this.state.visibleSection === 'two_seventy';
  }

  get twoSeventyOneActive() {
    return _.startsWith(this.state.visibleSection, 'two_seventy_one');
  }

  get twoSeventyOneActiveSpecialty() {
    return this.state.visibleSection.split('.').length === 3;
  }

  calculateInitialConfig() {
    const groupConfig = _.get(this.props, 'payer.payer_config.payer_config_settings.group');
    const groupYaml = _.isEmpty(groupConfig) ? '' : Yaml.safeDump(groupConfig);

    const twoSeventyConfig =
      _.get(this.props, 'payer.payer_config.payer_config_settings.two_seventy');
    const twoSeventyYaml = _.isEmpty(twoSeventyConfig) ? '' : Yaml.safeDump(twoSeventyConfig);

    const textAreaSections =  {
      sections: {
        group: groupYaml,
        two_seventy: twoSeventyYaml
      }
    };

    return payerToEditableFormat(_.merge(this.props.payer, textAreaSections));
  }

  displayErrors(errorObject) {
    if (!errorObject) {
      return null;
    }
    const errors = [];
    _.keys(errorObject).forEach(key => {
      const fieldName = FieldUtil.transformName(key);
      errorObject[key].forEach(error => {
        errors.push(`${fieldName} ${error}`);
      });
    });

    return (
      <div className="error">
        {errors.map((error, i) => <p key={i}>{error}</p>)}
      </div>
    );
  }

  updateTwoSeventyOneVisibleSection(specialtyKey, accum) {
    this.setState({
      visibleSection: `two_seventy_one.${specialtyKey}.${accum}`
    });
  }

  updateVisibleSection(section) {
    this.setState({ visibleSection: section });
  }

  handleChange = (setFieldValue, name, value) => setFieldValue(name, value, false);

  savePayer(values, { setSubmitting, setErrors }) {
    setErrors({ sections: null });
    const transformedValues = getSchema().cast(values);
    const config = toDbFormat(transformedValues.payer_config.payer_config_settings);

    const payerConfigAttributeValues = _.omit(transformedValues.payer_config, 'payer_config_settings');

    const data = {
      payer_id:              this.props.payer.payer_id,
      payer_config_attributes: {
        ...payerConfigAttributeValues,
        payer_config_settings: config
      }
    };

    of(this.props.editPayerAction(data))
      .pipe(
        catchError(error => of(error))
      ).subscribe(() => {
        setSubmitting(false);
      });
  }

  commitTextAreaValue(sectionName, { setFieldValue, setErrors, values }) {
    let data;
    try {
      data = Yaml.safeLoad(values.sections[sectionName]);
    } catch (e) {
      console.error(e);
      setErrors({ sections: { [sectionName]: `Invalid YAML. ${e.name}: ${e.message}` } });
      return;
    }
    setFieldValue(`payer_config.payer_config_settings.${sectionName}`, data);
    this.setState({ visibleTextArea: null });
  }

  launchTextArea(sectionName) {
    this.setState({ visibleTextArea: sectionName });
  }

  textAreaFor(sectionName) {
    return (
      <Field
        name={`sections.${sectionName}`}
        render={({ field }) => (
          <div>
            <textarea
              {...field}
              style={{ width: '100%', height: '500px', resize: 'none', fontFamily: 'monospace' }}
            />
            <ErrorMessage name={field.name} />
          </div>
        )}
      />
    );
  }

  textAreaButton(sectionName, { setFieldValue, setErrors, values }) {
    if (this.props.mode === 'view') return null;
    const isEditing = this.state.visibleTextArea === sectionName;
    return (
      <button
        className="section_link_button"
        type="button"
        onClick={isEditing ?
          () => this.commitTextAreaValue(sectionName, { setFieldValue, setErrors, values }) :
          () => this.launchTextArea(sectionName)
        }
      >
        {isEditing ? 'Done' : 'Edit'}
      </button>
    );
  }

  renderSectionNames({ config, setFieldValue, setErrors }) {
    return (
      <div className="specialty_category_wrapper">
        <h3
          className={`section_link ${this.costRulesActive ? 'active' : ''}`}
          onClick={() => this.updateVisibleSection('cost_rules')}
        >
          Cost Rules
        </h3>
        <h3
          className={`section_link ${this.groupMappingActive ? 'active' : ''}`}
          onClick={() => this.updateVisibleSection('group')}
        >
          <span>Group Mapping</span>
          {this.textAreaButton('group', { setFieldValue, setErrors, values: config })}
        </h3>
        <h3
          className={`section_link ${this.twoSeventyActive ? 'active' : ''}`}
          onClick={() => this.updateVisibleSection('two_seventy')}
        >
          <span>Two Seventy</span>
          {this.textAreaButton('two_seventy', { setFieldValue, setErrors, values: config })}
        </h3>
        <TwoSeventyOneHeader
          visibleSection={this.state.visibleSection}
          updateVisibleSection={this.updateVisibleSection}
          updateTwoSeventyOneVisibleSection={this.updateTwoSeventyOneVisibleSection}
        />
      </div>
    );
  }

  render() {
    return (
      <div>
        <Formik
          initialValues={this.initialPayerConfigValues}
          onSubmit={(values, actions) => this.savePayer(values, actions)}
          validateOnBlur={false}
          validateOnChange
          validationSchema={getSchema(this.props)}
          render={({ values, errors, touched, setFieldValue, setErrors, handleSubmit, isSubmitting, handleChange, status }) => (
            <form onSubmit={handleSubmit}>
              <h1>
                {`${this.props.payer.payer_nm} Payer`}
                {this.props.versionId ? ` Version ID: ${this.props.versionId}` : null}
              </h1>
              {this.props.notice ? <div className="error">{this.props.notice}</div> : null}
              {this.props.flashSuccess ? <div className="success">{this.props.flashSuccess}</div> : null}
              {!!status && <div className="error">{status}</div>}
              {this.displayErrors(_.get(this.props.messages, ['globalNotice', 'response', 'errors']))}
              {this.props.showPayerInfo &&
                <PayerInfo
                  payer={values}
                  handleChange={handleChange}
                  mode={this.props.mode}
                />
              }
              <h2>Payer Rules</h2>
              <div>
                {this.displayEditLink &&
                  <a
                    style={{ cursor: 'pointer' }}
                    href={`/payers/${this.props.payer.payer_id}/edit_config`}
                    className="edit_payer"
                  >
                      Edit
                  </a>
                }

              </div>
              <div className="payer_data" style={{ display: 'flex', flexDirection: 'row' }}>
                <div className="payer_rules_nav">
                  {this.renderSectionNames({ config: values, setErrors, setFieldValue })}
                </div>
                <div className="form_container" style={{ flexGrow: '5', flexBasis: '0' }}>
                    {this.costRulesActive &&
                      <CostRulesSection
                        costRulesData={_.get(this.payerRules, 'cost_rules')}
                        handleChange={handleChange}
                        mode={this.props.mode}
                      />
                    }
                    {this.groupMappingActive &&
                      (this.state.visibleTextArea === 'group' ?
                        this.textAreaFor('group') :
                        <GroupMappingSection
                          groupMappingData={_.get(values, 'payer_config.payer_config_settings.group')}
                        />
                      )
                    }
                    {this.twoSeventyActive &&
                      (this.state.visibleTextArea === 'two_seventy' ?
                        this.textAreaFor('two_seventy') :
                        <TwoSeventySection
                          twoSeventyData={_.get(values, 'payer_config.payer_config_settings.two_seventy')}
                          supportedSpecialties={_.get(this.props, ['supported_specialties'])}
                        />)
                    }

                    {this.twoSeventyOneActive && this.twoSeventyOneActiveSpecialty &&
                      <DataSection
                        mode={this.props.mode}
                        values={values}
                        visible={this.state.visible}
                        visibleSection={this.state.visibleSection}
                        handleChange={handleChange}
                        errors={errors}
                        touched={touched}
                        isSubmitting={isSubmitting}
                        setFieldValue={setFieldValue}
                        availableFilters={this.props.availableFilters}
                      />
                    }
                    <div className="form_actions">
                      <FormField mode={this.props.mode}>
                        <button
                          disabled={this.state.visibleTextArea != null}
                          className="button"
                          type="submit"
                        >
                            Save
                        </button>
                        <a
                          className="button secondary"
                          href={`/payers/${this.props.payer.payer_id}`}
                        >
                          Cancel
                        </a>
                      </FormField>
                      {this.props.isVersion &&
                        <span>
                          {this.props.canEdit &&
                            <button
                              className="button"
                              type="button"
                              onClick={() => this.props.restoreConfigVersionAction({
                                payer_id: this.props.payer.payer_id,
                                version_id: this.props.versionId
                              })}
                            >
                              Restore this version
                            </button>
                          }
                          <a
                            className="button"
                            href={`/payers/${this.props.payer.payer_id}/show_config`}
                          >
                            View active version
                          </a>
                        </span>
                      }
                    </div>
                </div>
              </div>
            </form>
          )}
        />
        {this.props.showVersions &&
          <PayerVersions
            payer_id={this.props.payer.payer_id}
            versions={this.props.payer.payer_config_versions}
          />
        }
      </div>
    );
  }
}

EditPayer.propTypes = {
  payer: PropTypes.object,
  mode: PropTypes.string,
  showPayerInfo: PropTypes.bool,
  showVersions: PropTypes.bool,
  editPayerAction: PropTypes.func,
  restoreConfigVersionAction: PropTypes.func,
  versionId: PropTypes.number,
  isVersion: PropTypes.bool,
  notice: PropTypes.string,
  flashSuccess: PropTypes.string,
  canEdit: PropTypes.bool,
  messages: PropTypes.object,
  availableFilters: PropTypes.array
};

const mapDispatchToProps = { editPayerAction, restoreConfigVersionAction };
const mapStateToProps = state => ({
  messages: state.rteEditPayer
});

export default connect(mapStateToProps, mapDispatchToProps)(EditPayer);
